import axios from "axios";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import AppLoading from "../components/shared/Loaders/HomeLoading/AppLoading";
import { FioeatGraphicChart } from "../config";
import { ApiResponse } from "../interfaces/ApiResponse";
import { loadConfig } from "../services/apiConfig";

export interface GraphicChart {
  id: number;
  companyId: number;
  primaryColor: string;
  secondaryColor: string;
  layoutColor: string;
  appLogoImage: string;
  appDarkLogoImage: string;
  appLightLogoImage: string;
  backgroundDesktopImage: string;
  backgroundTabletImage: string;
  backgroundMobileImage: string;
  establishementCount: number;
  establishementId: number | null;
  withCategoryWheel: boolean;
  hasDarkBackground: boolean;
  showSearchBar: boolean;
  isGlobalWebsite: boolean;
  cart_expiration_minutes?: number;
  showFidelity?: boolean;
  showCommandEnLigne ?: boolean;
}

interface ConfigContextType {
  config: GraphicChart | null;
  setConfig: React.Dispatch<React.SetStateAction<GraphicChart | null>>;
}

export const ConfigContext = createContext<ConfigContextType | undefined>(
  undefined
);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error("useConfig must be used within a ConfigProvider");
  }
  return context;
};

const initialConfig: GraphicChart = {
  id: 0,
  companyId: 0,
  primaryColor: "#EE8113",
  secondaryColor: "black",
  layoutColor: "grey",
  appLogoImage: "",
  appDarkLogoImage: "",
  appLightLogoImage: "",
  backgroundDesktopImage: "",
  backgroundTabletImage: "",
  backgroundMobileImage: "",
  establishementCount: 0,
  establishementId: 0,
  withCategoryWheel: false,
  hasDarkBackground: false,
  showSearchBar: false,
  isGlobalWebsite: true,
  cart_expiration_minutes: 60,
  showFidelity: true,
  showCommandEnLigne: true,
};

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<GraphicChart | null>(initialConfig);
  const [isLoading, setIsLoading] = useState(true);

  const fetchGraphicChart = async (
    apiCatalogue: string,
    website_url: string,
    isGlobalWebsite: boolean
  ): Promise<GraphicChart> => {
    try {
      if (isGlobalWebsite) {
        return FioeatGraphicChart;
      } else {
        const url = `${apiCatalogue}/v2/GetClickAndCollectGraphicChart?url=${website_url}`;
        // const url = `${apiCatalogue}/ClickAndCollectGraphicChart/GetClickAndCollectGraphicChart?companyId=66`;
        const response = await axios.get(url);
        const data: ApiResponse<GraphicChart> = response.data;
        const graphicChart = data.data.result;
        return graphicChart;
      }
    } catch (error) {
      console.error("Error fetching the graphic chart:", error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const config = await loadConfig();
        // console.log("🚀 ~ fetchConfig ~ config:", config)
        localStorage.removeItem("establishementToken");
        localStorage.setItem("api_baseUrl", config.api_baseUrl || "");
        localStorage.setItem("api_catalogue", config.api_catalogue || "");
        localStorage.setItem("key_google", config.key_google || "");
        localStorage.setItem(
          "stripe_public_key",
          config.stripe_public_key || ""
        );
        localStorage.setItem(
          "mapObject",
          JSON.stringify(config.mapObject || {})
        );

        const website_url = window.location.origin;

        const graphicChart = await fetchGraphicChart(
          config.api_catalogue,
          website_url,
          // "https://fioeat-dev.frenchinnov.fr",
          // "https://loko-riko.frenchinnov.fr",
          // "https://fivepizza.frenchinnov.fr",
          // "https://aubrazier.frenchinnov.fr",
          // "https://misterbeef.frenchinnov.fr",
          // "https://marvelous.frenchinnov.fr",
          // "https://factorys.frenchinnov.fr",
          // 'https://obrooklyn.frenchinnov.fr',
          // "https://harryscafe.frenchinnov.fr",
          // "https://bigbuddha.frenchinnov.fr",
          // "http://192.168.1.101",
          config.isGlobalWebsite
        );
        // console.log("🚀 ~ fetchConfig ~ graphicChart:", graphicChart)

        localStorage.setItem(
          "companyId",
          graphicChart?.companyId.toString() || "0"
        );
        setConfig({
          ...graphicChart,
          hasDarkBackground: config.hasDarkBackground,
          showSearchBar: config.showSearchBar,
          isGlobalWebsite: config.isGlobalWebsite,
          cart_expiration_minutes: config.cart_expiration_minutes,
        });

        document.documentElement.style.setProperty(
          "--primary-color",
          graphicChart.primaryColor
        );
        document.documentElement.style.setProperty(
          "--secondary-color",
          graphicChart.secondaryColor
        );
        document.documentElement.style.setProperty(
          "--layout-color",
          graphicChart.layoutColor
        );
      } catch (error) {
        console.error("Error loading configuration or colors:", error);

        document.documentElement.style.setProperty(
          "--primary-color",
          initialConfig.primaryColor
        );
        document.documentElement.style.setProperty(
          "--secondary-color",
          initialConfig.secondaryColor
        );
        document.documentElement.style.setProperty(
          "--layout-color",
          initialConfig.layoutColor
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  if (isLoading) {
    return <AppLoading />;
  }

  return (
    <ConfigContext.Provider value={{ config, setConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};
